import {Fragment, memo, useCallback, useEffect, useRef, useState} from 'react'
import {BranchName, Button, Dialog, Flash, Link, NavList, Spinner} from '@primer/react'
import {Blankslate} from '@primer/react/experimental'
import {AlertIcon, GitBranchIcon, XIcon} from '@primer/octicons-react'
import {useQueryClient} from '@github-ui/react-query'
import {announce} from '@github-ui/aria-live'

import {usePreviewStackData} from '../../page-data/loaders/use-preview-stack-data'
import type {PreviewStackData, PreviewStackPull} from '../../page-data/payloads/preview-stack'
import {useHeaderPageDataQueryKey} from '../../page-data/loaders/use-header-page-data'
import {useCreateStackOrAppendMutation} from '../../mutations/use-create-stack-or-append-mutation'
import {PullRequestState} from '../../types'
import {StateIcon} from '../StateIcon'
import type {StackCreatedInfo} from './StackCreatedBanner'
import styles from './PreviewStackDialog.module.css'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {buildPullRequestUserActionContext} from '../../analytics/pull-request-user-action'

export type PreviewStackDialogProps = {
  currentPullNumber: number
  onClose: () => void
  onStackCreated: (info: StackCreatedInfo) => void
  returnFocusRef: React.RefObject<HTMLButtonElement | null>
}

export function PreviewStackDialog({
  currentPullNumber,
  onClose,
  onStackCreated,
  returnFocusRef,
}: PreviewStackDialogProps) {
  const {data, isPending, isError} = usePreviewStackData()
  const [originalData, setOriginalData] = useState<PreviewStackData | undefined | null>(null)
  const [pulls, setPulls] = useState<PreviewStackPull[]>([])
  const [submitError, setSubmitError] = useState<string | null>(null)
  const {mutate: createStackOrAppend} = useCreateStackOrAppendMutation()
  const queryClient = useQueryClient()
  // Use custom submission state to prevent resubmission while updating the header page data
  const [isSubmitting, setIsSubmitting] = useState(false)
  const headerPageDataQueryKey = useHeaderPageDataQueryKey()
  const hasAnnouncedDataRef = useRef(false)

  if (data !== originalData) {
    setOriginalData(data)
    setPulls(data ?? [])
  }

  const isModified = originalData !== null && pulls.length !== (originalData?.length ?? 0)
  const hasPulls = pulls.length > 0
  // Failure covers both a fetch error and a successful response with no
  // pulls -- either way there's no preview to show.
  const showFailure = !isPending && (isError || !hasPulls)
  // The tail (bottom) pull carries the existing stack id when this preview
  // walked down into one. Pulls are top-first in display order, so the bottom
  // is the last element.
  const lastPull = pulls[pulls.length - 1]
  const existingStackId = lastPull?.stackId
  const existingStackNumber = lastPull?.stackNumber
  const isExtendingExistingStack = existingStackId != null
  const createStackLabel = isExtendingExistingStack ? 'Add to stack' : 'Create stack'
  const hasOtherNewlyAddedPulls = pulls.some(pull => pull.stackId == null && pull.number !== currentPullNumber)
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const listContainerRef = useRef<HTMLDivElement>(null)
  const pullsCount = pulls.length
  useEffect(() => {
    if (isPending || hasAnnouncedDataRef.current) return
    const element = listContainerRef.current ?? undefined
    if (showFailure) {
      announce('Preview failed to generate. Try refreshing the page.', {element})
    } else {
      announce(`Proposed stack loaded with ${pullsCount} pull ${pullsCount === 1 ? 'request' : 'requests'}.`, {element})
    }
    hasAnnouncedDataRef.current = true
  }, [isPending, showFailure, pullsCount])

  const handleRemoveTop = useCallback(() => {
    setPulls(prev => prev.slice(1))
  }, [])

  const handleReset = () => {
    sendClickAnalyticsEvent(
      buildPullRequestUserActionContext({
        category: 'stack_preview',
        action: 'reset',
      }),
    )
    if (originalData) setPulls(originalData)
  }

  const handleSubmit = () => {
    if (!hasPulls) return
    setSubmitError(null)
    setIsSubmitting(true)
    sendClickAnalyticsEvent(
      buildPullRequestUserActionContext({
        category: 'stack_preview',
        action: 'submit',
        data: {
          existing_stack_id: existingStackId ?? undefined,
          preview_stack_size: pulls.length,
        },
      }),
    )

    // Backend expects position ASC (bottom-first); display order is top-first,
    // so we iterate in reverse. When appending, skip pulls already in a stack
    // -- the controller loads the existing membership from `stackId` itself.
    const pullRequestIds: number[] = []
    for (let i = pulls.length - 1; i >= 0; i--) {
      const pull = pulls[i]
      if (!pull) continue
      if (isExtendingExistingStack && pull.stackId != null) continue
      pullRequestIds.push(pull.id)
    }
    if (pullRequestIds.length === 0) return
    createStackOrAppend(
      {
        pullRequestIds,
        stackId: existingStackId,
      },
      {
        onSuccess: async response => {
          try {
            await queryClient.refetchQueries({queryKey: headerPageDataQueryKey}, {throwOnError: true})
            onStackCreated({stackNumber: response.stackNumber, appended: isExtendingExistingStack})
            onClose()
          } catch {
            setSubmitError('Something unexpected happened. Please refresh the page.')
            setIsSubmitting(false)
          }
        },
        onError: error => {
          setSubmitError(error.message ?? 'Failed to create stack.')
          setIsSubmitting(false)
        },
      },
    )
  }

  const footer = (
    <Dialog.Footer>
      <div className={styles.footerLayout}>
        {isModified ? (
          <Button
            variant="danger"
            onClick={handleReset}
            disabled={isSubmitting}
            title="Reset to the original proposed stack"
          >
            Reset stack
          </Button>
        ) : null}
        <div className={styles.footerActions}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={isSubmitting}
            loadingAnnouncement={isExtendingExistingStack ? 'Adding to stack' : 'Creating stack'}
            onClick={handleSubmit}
            disabled={isPending || !hasPulls}
          >
            {createStackLabel}
          </Button>
        </div>
      </div>
    </Dialog.Footer>
  )

  return (
    <Dialog title="Preview stack" returnFocusRef={returnFocusRef} onClose={onClose} renderFooter={() => footer}>
      <PreviewStackDescription
        isExtendingExistingStack={isExtendingExistingStack}
        existingStackNumber={existingStackNumber}
        hasOtherNewlyAddedPulls={hasOtherNewlyAddedPulls}
      />
      {submitError ? (
        <Flash variant="danger" className="mb-2">
          {submitError}
        </Flash>
      ) : null}
      <PreviewStackList
        pulls={pulls}
        isPending={isPending}
        showFailure={showFailure}
        onRemoveTop={handleRemoveTop}
        disableRemove={isSubmitting}
        currentPullNumber={currentPullNumber}
        containerRef={listContainerRef}
      />
    </Dialog>
  )
}

type PreviewStackDescriptionProps = {
  isExtendingExistingStack: boolean
  existingStackNumber?: number | null
  hasOtherNewlyAddedPulls: boolean
}

function PreviewStackDescription({
  isExtendingExistingStack,
  existingStackNumber,
  hasOtherNewlyAddedPulls,
}: PreviewStackDescriptionProps) {
  const learnMoreLink = (
    <Link inline href="https://gh.io/stacks-overview">
      Learn more about pull request stacks
    </Link>
  )

  if (isExtendingExistingStack && existingStackNumber != null) {
    return (
      <p className="mb-3 fgColor-muted">
        <span>This pull request</span>
        {hasOtherNewlyAddedPulls ? <span> and other pull requests</span> : null}
        <span> can be added to stack #{existingStackNumber}. Review and update the stack when you’re ready. </span>
        {learnMoreLink}
      </p>
    )
  }

  return (
    <p className="mb-3 fgColor-muted">
      This pull request can be stacked with other pull requests. Review and create the stack when you’re ready.{' '}
      {learnMoreLink}
    </p>
  )
}

type PreviewStackListProps = {
  pulls: PreviewStackPull[]
  isPending: boolean
  showFailure: boolean
  onRemoveTop: () => void
  disableRemove: boolean
  currentPullNumber: number
  containerRef: React.RefObject<HTMLDivElement | null>
}

function PreviewStackList({
  pulls,
  isPending,
  showFailure,
  onRemoveTop,
  disableRemove,
  currentPullNumber,
  containerRef,
}: PreviewStackListProps) {
  const hasPulls = !isPending && pulls.length > 0
  const lastIndex = pulls.length - 1
  // Pulls are top-first: newly-added pulls sit above the existing stack they
  // are being appended to, so any already-stacked pulls form a contiguous
  // suffix. `firstExistingIndex` is the boundary (also the count of
  // newly-added pulls); when it is -1 there is no existing stack
  const firstExistingIndex = pulls.findIndex(pull => pull.stackId != null)
  const hasExistingStack = firstExistingIndex !== -1
  // Pulls already in an existing stack collectively count as one stackable
  // unit, since removing the top standalone pull would still leave a valid
  // 2-pull stack (the existing one).
  const stackablePullCount = hasExistingStack ? firstExistingIndex + 1 : pulls.length
  const canRemoveTop = stackablePullCount > 2

  return (
    <div
      ref={containerRef}
      role="region"
      className={styles.stackContainer}
      aria-label="Proposed stack of pull requests"
      aria-busy={isPending}
    >
      {isPending ? (
        <Spinner srText="Loading proposed stack" />
      ) : showFailure ? (
        <Blankslate border={false} spacious={false}>
          <Blankslate.Visual>
            <AlertIcon size="medium" className="fgColor-muted" />
          </Blankslate.Visual>
          <Blankslate.Heading as="h2">Preview failed to generate</Blankslate.Heading>
          <Blankslate.Description>Try refreshing the page.</Blankslate.Description>
        </Blankslate>
      ) : hasPulls ? (
        <NavList className={styles.stackNavList}>
          {pulls.map((pull, index) => {
            const onRemove = canRemoveTop && index === 0 ? onRemoveTop : undefined
            const isCurrent = pull.number === currentPullNumber
            const dividerStackNumber = hasExistingStack && index === firstExistingIndex ? pull.stackNumber : null
            return (
              <Fragment key={pull.number}>
                {dividerStackNumber != null ? <ExistingStackDivider stackNumber={dividerStackNumber} /> : null}
                <PreviewStackPullItem
                  pull={pull}
                  isCurrent={isCurrent}
                  isTail={index === lastIndex}
                  onRemove={onRemove}
                  disableRemove={disableRemove}
                />
              </Fragment>
            )
          })}
        </NavList>
      ) : null}
    </div>
  )
}

function ExistingStackDivider({stackNumber}: {stackNumber: number}) {
  const label = `Already in stack #${stackNumber}`
  return (
    <li aria-label={label} className={styles.groupDivider}>
      <span className={styles.groupDividerLabel}>{label}</span>
    </li>
  )
}

const PreviewStackPullItem = memo(
  // eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name for readable stack traces
  function PreviewStackPullItem({
    pull,
    isCurrent,
    isTail,
    onRemove,
    disableRemove,
  }: {
    pull: PreviewStackPull
    isCurrent: boolean
    isTail: boolean
    onRemove?: () => void
    disableRemove: boolean
  }) {
    const {sendClickAnalyticsEvent} = useClickAnalytics()
    const destinationBranch = isTail ? pull.baseBranch : null

    return (
      <>
        <NavList.Item href={pull.url} aria-current={isCurrent ? 'page' : undefined}>
          <NavList.LeadingVisual>
            <StateIcon state={pull.state ?? PullRequestState.Open} />
          </NavList.LeadingVisual>
          <div className={styles.prItemContent}>
            <span className={styles.prTitle}>{pull.title}</span>
            <span className={styles.prMeta}>
              <span className="text-normal no-wrap">#{pull.number}</span>
              <BranchName as="span" className={styles.prBranchName}>
                {pull.headBranch}
              </BranchName>
            </span>
          </div>
          {onRemove ? (
            <NavList.TrailingAction
              as="button"
              icon={XIcon}
              label={
                isCurrent
                  ? "You can't remove the currently viewed pull request"
                  : `Remove #${pull.number} from preview stack`
              }
              disabled={disableRemove || isCurrent}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault()
                event.stopPropagation()
                sendClickAnalyticsEvent(
                  buildPullRequestUserActionContext({
                    category: 'stack_preview',
                    action: 'remove',
                  }),
                )
                onRemove()
              }}
            />
          ) : null}
        </NavList.Item>
        {isTail && destinationBranch ? (
          <li className={styles.branchItem}>
            <span className={styles.branchItemContent}>
              <GitBranchIcon className="fgColor-muted" />
              <BranchName as="span">{destinationBranch}</BranchName>
            </span>
          </li>
        ) : null}
      </>
    )
  },
)
